import { FieldsConfig, FieldConfig } from "@/components/layouts/DetailFields";
import { formatMonetaryDisplayValue, toPercentInput } from "@/utils/numbers";
import { APIFieldItem, SettingsFieldType } from "@/pages/settings/types";
import { EntityType } from "@/services/hooks/entities/useEntityType";
import { DrawerStateItem } from "@/services/hooks/entities/useQuickViewDrawer";
import { TFunction } from "i18next";
import ConfidenceLevelIcon from "@/components/controls/ConfidenceLevelIcon";
import {
  dealConfidenceLevels,
  csmRenewalConfidenceLevels
} from "@/pages/deals/helpers";
import {
  DealConfidenceLevel,
  CsmRenewalConfidenceLevel,
  DealStatus
} from "@/pages/deals/types";
import { SalesExplorationStatus } from "@/pages/leads/types";
import { ContractRenewalType } from "@/pages/contracts/types";
import { getEnumTKey } from "@/utils/strings";
import { daysToWeeks } from "date-fns";
import { useThresholds } from "@/entities/settings/hooks";
import { SettingsThresholdsType } from "@/entities/settings/types";
import {
  isEntityDisplayType,
  isLicensesDisplayType,
  isPeopleDisplayType
} from "@/utils/entityFieldHelpers";
import { FieldDisplayType } from "@/pages/settings/components/dealsSettings/FieldManagement/types";
import {
  parseOptions,
  getValidationForDisplayType,
  formatSCOFieldValue,
  createAddNewOptionHandler,
  shouldShowCreateNewButton,
  shouldDisableCreateNewButton,
  formatMultiSelectValueForDisplay,
  getConfiguredCurrencySymbol
} from "./shared";
import {
  allowsMultipleFiles,
  getFileFieldAccept,
  parseFileFieldValue
} from "@/utils/entityFieldsUtils/fileFieldUtils";
import { get, isNull, isUndefined, map } from "lodash";
import { camelizeFieldKey } from "@/utils/fieldKeys";
import { isIcpRegistryField } from "@/entities/icpFit/helpers";
import { AddButtonFooter } from "@/components/ui/add-button-footer";
import { getCountrySelectOptions } from "@/utils/countries";
import { getCurrentLocale } from "@/utils/locale";

const isValidDisplayValue = (value: unknown): value is string | number =>
  value !== null && value !== undefined && value !== "";

const getDisplayValueOrFallback = (
  value: string | number | null | undefined,
  fallback: string
) => (isValidDisplayValue(value) ? String(value) : fallback);

// A linked field's display value can arrive on the entity three ways: the
// resolved companion field the API adds, the target field itself, or the raw
// source value. Text and file renderings read the same chain.
const resolveLinkedDisplayValue = (
  entityData: Record<string, unknown>,
  fieldKey: string,
  targetField: string
): unknown => {
  // fieldKey is camelCase from API (e.g. "companyIndustryId") — append "_resolved" and
  // re-camelize to get the entity data key (e.g. "companyIndustryIdResolved")
  const resolvedValue = get(
    entityData,
    camelizeFieldKey(`${fieldKey}_resolved`)
  );
  if (isValidDisplayValue(resolvedValue)) {
    return resolvedValue;
  }

  const targetValue = get(entityData, targetField);
  if (isValidDisplayValue(targetValue)) {
    return targetValue;
  }

  return get(entityData, fieldKey);
};

const getTableNameToEntityType = (): Record<string, EntityType> => ({
  companies: EntityType.COMPANY,
  people: EntityType.PERSON,
  leads: EntityType.LEAD,
  deals: EntityType.DEAL,
  users: EntityType.USER
});

const createLinkedFieldConfig = (
  apiField: APIFieldItem,
  entityData: any,
  t: TFunction,
  isSCO: boolean = false
): FieldConfig | null => {
  const linkedDataInfo = apiField?.linkedDataInfo;
  const unknownText = t("unknown");

  const isFileUpload = apiField.displayType === "fileUpload";

  if (!linkedDataInfo) {
    if (isFileUpload) {
      return {
        label: apiField.label,
        type: "readOnlyAttachments",
        fieldKey: apiField.fieldKey,
        emptyText: unknownText
      };
    }
    if (apiField.displayType === "multiSelect") {
      return {
        label: apiField.label,
        type: "readOnlyText",
        fieldKey: apiField.fieldKey,
        formatter: (value) =>
          formatMultiSelectValueForDisplay(value, unknownText) || unknownText
      };
    }
    return {
      label: apiField.label,
      type: "readOnlyText",
      fieldKey: apiField.fieldKey,
      formatter: (value) => getDisplayValueOrFallback(value, unknownText)
    };
  }

  const { targetTable, sourceKey, targetField } = linkedDataInfo;

  const linkedEntityType = getTableNameToEntityType()[targetTable];
  if (!linkedEntityType) {
    if (isFileUpload) {
      return {
        label: apiField.label,
        type: "readOnlyAttachments",
        emptyText: unknownText
      };
    }
    return {
      label: apiField.label,
      type: "readOnlyText",
      formatter: (value) => getDisplayValueOrFallback(value, unknownText)
    };
  }

  const linkedEntityId = get(entityData, sourceKey);
  if (!linkedEntityId) {
    return {
      label: apiField.label,
      type: "readOnlyText",
      formatter: () => unknownText
    };
  }

  // If it's an SCO field, render it read-only instead of as a linkedField
  if (isSCO) {
    const getSCOTargetValue = (value: unknown) =>
      get(entityData, targetField) ?? get(value, targetField);

    if (isFileUpload) {
      return {
        label: apiField.label,
        type: "readOnlyAttachments",
        emptyText: unknownText,
        attachmentsResolver: (value) =>
          parseFileFieldValue(getSCOTargetValue(value))
      };
    }
    return {
      label: apiField.label,
      type: "readOnlyText",
      formatter: (value) =>
        getDisplayValueOrFallback(getSCOTargetValue(value), unknownText)
    };
  }

  const linkedEntity: DrawerStateItem = {
    id: linkedEntityId,
    name: ""
  } as DrawerStateItem;

  // A linked file field lists the source entity's files as read-only chips —
  // each one still opens and downloads, but the value belongs to that entity so
  // add and remove stay out. The source entity travels with the config so the
  // row can offer "Edit in {Entity}", the way every other linked field does.
  if (isFileUpload) {
    return {
      label: apiField.label,
      type: "readOnlyAttachments",
      emptyText: unknownText,
      linkedEntityType,
      linkedEntity,
      attachmentsResolver: () =>
        parseFileFieldValue(
          resolveLinkedDisplayValue(entityData, apiField.fieldKey, targetField)
        )
    };
  }

  const isMultiSelect = apiField.displayType === "multiSelect";
  // A linked field carries its target field's display type and config, so a
  // currency target renders as money here just as it does on its own entity.
  const isCurrency = apiField.displayType === FieldDisplayType.CURRENCY;
  const isPercentage = apiField.displayType === FieldDisplayType.PERCENTAGE;

  return {
    label: apiField.label,
    type: "linkedField",
    linkedEntityType,
    linkedEntity,
    displayType: apiField.displayType,
    formatter: () => {
      const displayValue = resolveLinkedDisplayValue(
        entityData,
        apiField.fieldKey,
        targetField
      );

      if (!isValidDisplayValue(displayValue)) {
        return unknownText;
      }

      if (isMultiSelect) {
        return (
          formatMultiSelectValueForDisplay(displayValue, unknownText) ||
          unknownText
        );
      }

      if (isCurrency) {
        // The resolved value can arrive as a float string ("14630.0"); drop an
        // all-zero decimal tail so it formats like the target field's own
        // "$14,630" while "730.50" keeps its cents.
        const moneyValue = String(displayValue).replace(/\.0+$/, "");
        return (
          formatMonetaryDisplayValue(moneyValue, {
            isMonetary: true,
            currencySymbol: getConfiguredCurrencySymbol(apiField.config)
          }) || unknownText
        );
      }

      if (isPercentage) {
        // Stored as a fraction; the target field's own detail row shows it in
        // 1–100 space with a " %" suffix, so do the same here.
        const percentValue = toPercentInput(displayValue);
        if (percentValue === null) {
          return unknownText;
        }
        return `${formatMonetaryDisplayValue(String(percentValue), {})} %`;
      }

      return String(displayValue);
    }
  };
};

const createSCOFieldConfig = (
  apiField: APIFieldItem,
  t: TFunction,
  thresholds?: ReturnType<typeof useThresholds>["thresholds"]
): FieldConfig | null => {
  const baseConfig = {
    label: apiField.label
  };

  if (apiField.fieldType === "system") {
    switch (apiField.fieldKey) {
      case "keyStakeholdersMappingScore":
        return {
          ...baseConfig,
          type: "scoreBar",
          suffix: "%",
          levels: thresholds?.find(
            (threshold) =>
              threshold.id ===
              SettingsThresholdsType.KEY_STAKEHOLDERS_MAPPING_SCORE
          )
        };

      case "championsMappingScore":
        return {
          ...baseConfig,
          type: "strengthBar",
          getLabel: (scoreStrength) => t(`champions.levels.${scoreStrength}`),
          scoreStrengthThresholds: thresholds?.find(
            (t) => t.id === SettingsThresholdsType.CHAMPIONS_MAPPING_SCORE
          )
        };

      case "daysInStage":
        return {
          ...baseConfig,
          type: "readOnlyText",
          formatter: (value) =>
            isNull(value) || isUndefined(value) || value < 0
              ? ""
              : `${daysToWeeks(value).toString()} ${t("stageAverageDuration.weeksUnit")}`
        };
    }
  }

  // Convert all other SCO fields to readOnlyText with simple formatting
  switch (apiField.displayType) {
    case "textarea":
      return {
        ...baseConfig,
        type: "textarea",
        additionalProps: {
          useTextarea: true,
          minRows: 3,
          disabled: true // Make textarea disabled for SCO fields to preserve markdown rendering
        }
      };

    case "date":
      return {
        ...baseConfig,
        type: "readOnlyDate"
      };

    case "url":
      return {
        ...baseConfig,
        type: "clickableUrl",
        validation: getValidationForDisplayType(
          apiField.displayType,
          t,
          apiField.config
        )
      };

    case "fileUpload":
      return {
        ...baseConfig,
        type: "readOnlyAttachments",
        emptyText: t("unknown")
      };

    default:
      return {
        ...baseConfig,
        type: "readOnlyText",
        formatter: (value) => formatSCOFieldValue(value, t)
      };
  }
};

// Date fields that record when something happened, so they show the viewer's local
// day. Every other date field (expected close, contract dates, custom dates) is a
// calendar date and keeps its stored day even when stored with a time.
export const LOCAL_DAY_FIELD_KEYS = new Set([
  "createdAt",
  "updatedAt",
  "closedAt",
  "statusChangedAt",
  "salesExplorationMeetingDate"
]);

export const createFieldConfigFromAPI = (
  apiField: APIFieldItem,
  entityData: any,
  entityType: EntityType,
  t: TFunction,
  thresholds?: ReturnType<typeof useThresholds>["thresholds"],
  context?: any, // handles modal functionality
  isNewFieldsUI = false
): FieldConfig | null => {
  const baseConfig = {
    label: apiField.label,
    helpText: apiField.helpText || undefined,
    required: apiField.required
  };

  // Check if this is an SCO field or linked field that should be read-only
  const isSCO = apiField.fieldType === SettingsFieldType.SCO;
  const isLinked = apiField.fieldType === SettingsFieldType.LINKED;

  if (isLinked) {
    return createLinkedFieldConfig(apiField, entityData, t, isSCO);
  }

  if (isSCO) {
    return createSCOFieldConfig(apiField, t, thresholds);
  }

  if (apiField.fieldKey === "renewalType") {
    return {
      ...baseConfig,
      type: "readOnlyText",
      formatter: (value) =>
        isValidDisplayValue(value)
          ? t(
              getEnumTKey(
                "contracts",
                "table.renewalTypeValues",
                ContractRenewalType,
                value as ContractRenewalType
              )
            )
          : t("unknown")
    };
  }

  if (apiField.fieldType === "system") {
    switch (apiField.fieldKey) {
      case "aeConfidenceLevel":
        return {
          ...baseConfig,
          type: "select",
          options: map(dealConfidenceLevels, (level) => ({
            id: level,
            name: DealConfidenceLevel[level]
          })),
          formatter: (value) =>
            t(
              getEnumTKey(
                "deals",
                "levels",
                DealConfidenceLevel,
                value as number
              )
            ),
          renderOptionIcon: (value) => (
            <ConfidenceLevelIcon value={Number(value)} />
          )
        };

      case "csmRenewalConfidenceLevel":
        return {
          ...baseConfig,
          type: "select",
          options: map(csmRenewalConfidenceLevels, (level) => ({
            id: level,
            name: CsmRenewalConfidenceLevel[level]
          })),
          formatter: (value) =>
            t(
              getEnumTKey(
                "deals",
                "levels",
                CsmRenewalConfidenceLevel,
                value as number
              )
            ),
          renderOptionIcon: (value) => (
            <ConfidenceLevelIcon value={Number(value)} />
          )
        };

      case "daysInStage":
        return {
          ...baseConfig,
          type: "readOnlyText",
          formatter: (value) =>
            isNull(value) || isUndefined(value) || value < 0
              ? ""
              : `${daysToWeeks(value).toString()} ${t("stageAverageDuration.weeksUnit")}`
        };

      case "keyStakeholdersMappingScore":
        return {
          ...baseConfig,
          type: "scoreBar",
          suffix: "%",
          levels: thresholds?.find(
            (threshold) =>
              threshold.id ===
              SettingsThresholdsType.KEY_STAKEHOLDERS_MAPPING_SCORE
          )
        };

      case "championsMappingScore":
        return {
          ...baseConfig,
          type: "strengthBar",
          getLabel: (scoreStrength) => t(`champions.levels.${scoreStrength}`),
          scoreStrengthThresholds: thresholds?.find(
            (t) => t.id === SettingsThresholdsType.CHAMPIONS_MAPPING_SCORE
          )
        };

      case "salesExplorationStatus":
        return {
          ...baseConfig,
          type: "readOnlyText",
          formatter: (value) =>
            value
              ? t(
                  getEnumTKey(
                    "leads",
                    "salesExplorationStatusValues",
                    SalesExplorationStatus,
                    value as SalesExplorationStatus
                  )
                )
              : t("unknown")
        };
    }
  }

  if (
    (apiField.readOnly || apiField.fieldType === SettingsFieldType.FORMULA) &&
    !isEntityDisplayType(apiField.displayType)
  ) {
    if (apiField.displayType === "url") {
      return {
        ...baseConfig,
        type: "clickableUrl",
        readOnly: true,
        additionalProps: {
          placeholder: ""
        }
      };
    }
    if (apiField.displayType === "date") {
      return {
        ...baseConfig,
        type: "readOnlyDate",
        emptyText: "",
        localDay: LOCAL_DAY_FIELD_KEYS.has(apiField.fieldKey)
      };
    }
    if (apiField.displayType === "fileUpload") {
      // The stored value is serialized attachment JSON — the generic formatter
      // below would render it verbatim instead of the files.
      return {
        ...baseConfig,
        type: "readOnlyAttachments",
        fieldKey: apiField.fieldKey,
        emptyText: ""
      };
    }
    return {
      ...baseConfig,
      type: "readOnlyText",
      fieldKey: apiField.fieldKey,
      formatter: (value: string | number | null | undefined) => {
        // A blank read-only value renders empty, matching the table cell —
        // "Unknown" would read as missing data rather than no value.
        if (value === null || value === undefined || value === "") {
          return "";
        }
        // Currency rollups (e.g. cmrr = carr / 12) are stored unrounded;
        // formatMonetaryDisplayValue is the only place that rounds them for display.
        if (apiField.displayType === FieldDisplayType.CURRENCY) {
          return (
            formatMonetaryDisplayValue(String(value), { isMonetary: true }) ||
            t("unknown")
          );
        }
        return String(value);
      }
    };
  }

  if (isEntityDisplayType(apiField.displayType)) {
    if (
      apiField.displayType === FieldDisplayType.PARTNER_CONTACTS &&
      entityType === EntityType.PARTNER
    ) {
      const partnerContactsConfig: any = {
        ...baseConfig,
        type: "multiSelect",
        options: [],
        isEntityField: true,
        displayType: apiField.displayType,
        // Base's "preview" is now "inline": it selects renderNewPreviewTrigger
        // under the flag and renderLegacyPreviewTrigger without it, so one value
        // covers both states. Flag-off "form" sent this to the boxed form look.
        variant: "inline",
        dependentValues: { partner: entityData?.partner }
      };
      if (apiField.config?.allowUsersToAdd) {
        partnerContactsConfig.config = apiField.config;
      }
      return partnerContactsConfig;
    }

    const entitySelectConfig: any = {
      ...baseConfig,
      type: "select",
      options: [],
      isEntityField: true,
      displayType: apiField.displayType,
      // "default" is the pre-redesign name for this look; SingleSelect keys
      // hasDefaultLook on it so flag-off renders what base rendered. "inline"
      // is the redesigned name for the same placement, so the flag swaps the
      // name rather than the branch.
      variant: isNewFieldsUI ? "inline" : "default"
    };

    if (apiField.config?.allowUsersToAdd) {
      entitySelectConfig.config = apiField.config;
    }

    // Add dependent values for licenses fields
    if (isLicensesDisplayType(apiField.displayType)) {
      entitySelectConfig.dependentValues = {
        productId: entityData?.productId
      };
    }

    // Add dependent values for people fields
    if (isPeopleDisplayType(apiField.displayType)) {
      entitySelectConfig.dependentValues = {
        companyId: entityData?.companyId
      };
      if (apiField.fieldKey === "reportsTo") {
        entitySelectConfig.showFixedOptions = true;
        if (entityData?.id) {
          entitySelectConfig.excludeIds = [entityData.id];
        }
      }
    }

    // Add dependent values for partner contact fields
    if (apiField.displayType === FieldDisplayType.PARTNER_CONTACTS) {
      entitySelectConfig.dependentValues = { partner: entityData?.partner };
    }

    return entitySelectConfig;
  }

  switch (apiField.displayType) {
    case "date": {
      const localDay = LOCAL_DAY_FIELD_KEYS.has(apiField.fieldKey);
      if (apiField.dataSource === "system") {
        return { ...baseConfig, type: "readOnlyDate", localDay };
      }
      return {
        ...baseConfig,
        type: "date",
        localDay,
        // Required only on the create form; the column is nullable and undated deals exist.
        clearable:
          entityType === EntityType.DEAL &&
          apiField.fieldKey === "expectedCloseAt"
      };
    }

    case "singleSelect":
      const shouldShowCreateNew = apiField.config?.allowUsersToAdd || false;
      const fieldKey = apiField.fieldKey;
      const fieldId = apiField.id;

      return {
        ...baseConfig,
        type: "select",
        options: parseOptions(apiField.config),
        showSearch: shouldShowCreateNew,
        additionalProps: {
          popOverFooter:
            shouldShowCreateNew && context
              ? (searchTerm: string) => {
                  const showButton = shouldShowCreateNewButton(searchTerm);
                  if (!showButton) {
                    return <div style={{ display: "none" }} />;
                  }

                  const isDisabled = shouldDisableCreateNewButton(
                    searchTerm,
                    parseOptions(apiField.config)
                  );

                  return (
                    <AddButtonFooter
                      buttonText={t(
                        "settings.userManagement.addNewUserModal.createNewOption"
                      )}
                      onClick={createAddNewOptionHandler(
                        context,
                        fieldId,
                        fieldKey,
                        searchTerm
                      )}
                      variant="iconNoBg"
                      disabled={isDisabled}
                    />
                  );
                }
              : undefined
        }
      };

    case "multiSelect":
      const shouldShowCreateNewMulti =
        apiField.config?.allowUsersToAdd || false;
      const fieldKeyMulti = apiField.fieldKey;
      const fieldIdMulti = apiField.id;

      return {
        ...baseConfig,
        type: "multiSelect",
        options: parseOptions(apiField.config),
        defaultValue: apiField.config?.defaultValue || [],
        showSearch: shouldShowCreateNewMulti,
        additionalProps: {
          popOverFooter:
            shouldShowCreateNewMulti && context
              ? (searchTerm: string) => {
                  const showButton = shouldShowCreateNewButton(searchTerm);
                  if (!showButton) {
                    return <div style={{ display: "none" }} />;
                  }

                  const isDisabled = shouldDisableCreateNewButton(
                    searchTerm,
                    parseOptions(apiField.config)
                  );

                  return (
                    <AddButtonFooter
                      buttonText={t(
                        "settings.userManagement.addNewUserModal.createNewOption"
                      )}
                      onClick={createAddNewOptionHandler(
                        context,
                        fieldIdMulti,
                        fieldKeyMulti,
                        searchTerm
                      )}
                      variant="iconNoBg"
                      disabled={isDisabled}
                    />
                  );
                }
              : undefined
        }
      };

    case "switch":
      return {
        ...baseConfig,
        type: "switch",
        options: [
          {
            id: "option1",
            displayName: apiField.config?.option1 || "Option 1"
          },
          {
            id: "option2",
            displayName: apiField.config?.option2 || "Option 2"
          }
        ],
        defaultValue: apiField.config?.defaultValue || "option1"
      };

    case "checkListItem":
      return {
        ...baseConfig,
        type: "checklistItem",
        defaultValue: apiField.config?.defaultValue === "checked"
      };

    case "checkbox":
      return {
        ...baseConfig,
        type: "checkbox",
        defaultValue: apiField.config?.defaultValue === "checked",
        additionalProps: {
          className: "whitespace-normal"
        }
      };

    case "textarea":
      return {
        ...baseConfig,
        type: "textarea",
        additionalProps: {
          useTextarea: true,
          minRows: 3
        }
      };

    case "number":
      return {
        ...baseConfig,
        type: "editableInput",
        validation: getValidationForDisplayType(
          apiField.displayType,
          t,
          apiField.config
        ),
        additionalProps: {
          type: "number",
          min: apiField.config?.minValue
            ? Number(apiField.config.minValue)
            : undefined,
          max: apiField.config?.maxValue
            ? Number(apiField.config.maxValue)
            : undefined,
          step: "any",
          className: "whitespace-normal"
        }
      };

    case "percentage":
      return {
        ...baseConfig,
        type: "editableInput",
        isPercentage: true,
        validation: getValidationForDisplayType(
          apiField.displayType,
          t,
          apiField.config
        ),
        additionalProps: {
          min: 0,
          max: 100,
          step: "any",
          className: "whitespace-normal"
        },
        suffixComponent: <span>%</span>
      };

    case "email":
      // Check if multiple emails are allowed
      if (apiField.config?.allowMultipleEmails) {
        return {
          ...baseConfig,
          type: "editableMultiInput",
          validation: getValidationForDisplayType(
            apiField.displayType,
            t,
            apiField.config
          ),
          additionalProps: {
            maxEntries: apiField.config?.maxEntries,
            placeholder: apiField.config?.placeholder,
            allowAddNew: apiField.config?.allowAddNew !== false,
            // Routes each entry to SingleInput's own email affordance — the
            // copy button and Mail hover icon the single email field uses.
            // Without it the rows fall back to Text's copyable treatment,
            // which is a different button with different copied feedback.
            isEmail: true
          }
        };
      }

      return {
        ...baseConfig,
        type: "editableInput",
        validation: getValidationForDisplayType(
          apiField.displayType,
          t,
          apiField.config
        ),
        additionalProps: {
          type: "email"
        }
      };

    case "url":
      return {
        ...baseConfig,
        type: "clickableUrl",
        validation: getValidationForDisplayType(
          apiField.displayType,
          t,
          apiField.config
        )
      };

    case "dateRange":
      return {
        ...baseConfig,
        type: "dateRange"
      };

    case "button":
      return {
        ...baseConfig,
        type: "button",
        config: {
          hook_id: apiField.config?.hook_id || "",
          loading_text: apiField.config?.loading_text || "Loading...",
          button_label:
            apiField.config?.button_label || apiField.label || "Execute",
          variant: apiField.config?.variant || "secondary",
          mode: apiField.config?.mode,
          url: apiField.config?.url,
          open_target: apiField.config?.open_target,
          popup_width: apiField.config?.popup_width,
          popup_height: apiField.config?.popup_height
        }
      };

    case FieldDisplayType.COUNTRIES:
      return {
        ...baseConfig,
        type: "select",
        options: getCountrySelectOptions(
          getCurrentLocale(),
          get(entityData, apiField.fieldKey)
        ),
        showSearch: true
      };

    case FieldDisplayType.PHONE:
      return {
        ...baseConfig,
        type: "phone"
      };

    case "currencies":
      return {
        ...baseConfig,
        type: "currencySelect"
      };

    case "fileUpload":
      return {
        ...baseConfig,
        type: "attachments",
        accept: getFileFieldAccept(apiField.config?.supportedFiles),
        isSingleFile: !allowsMultipleFiles(apiField.config)
      };

    case "currency":
      const initialValue =
        entityType === EntityType.DEAL &&
        apiField.fieldKey === "dealValueAmount"
          ? entityData?.dealValueAmount || 0
          : undefined;
      return {
        ...baseConfig,
        type: "editableInput",
        initialValue,
        isMonetary: true,
        currencySymbol: getConfiguredCurrencySymbol(apiField.config),
        validation: getValidationForDisplayType(
          apiField.displayType,
          t,
          apiField.config
        )
      };

    case "text":
      return {
        ...baseConfig,
        type: "editableInput",
        additionalProps: { className: "whitespace-normal break-words" }
      };

    default:
      return {
        ...baseConfig,
        type: "editableInput",
        additionalProps: { className: "whitespace-normal break-words" }
      };
  }
};

/**
 * Creates a complete fields configuration for detail views from API fields
 */
export const createFieldsConfigFromAPI = (
  apiFields: APIFieldItem[] | undefined,
  entityData: any,
  entityType: EntityType,
  t: TFunction,
  thresholds?: ReturnType<typeof useThresholds>["thresholds"],
  context?: any,
  isNewFieldsUI = false
): FieldsConfig => {
  if (!apiFields) return {};

  const orderedConfig: FieldsConfig = {};

  // The partner-contact field only shows once a partner is selected on the
  // deal/lead; resolve the partner field key up front to gate its visibility.
  const gatingPartnerFieldKey = apiFields.find(
    (field) => field.displayType === FieldDisplayType.PARTNERS
  )?.fieldKey;

  apiFields
    .filter((field) => {
      if (!field.showInDetailedView) {
        return false;
      }
      if (field.fieldType === "header") {
        return false;
      }
      if (isIcpRegistryField(field)) {
        return false;
      }
      if (
        field.displayType === FieldDisplayType.PARTNER_CONTACTS &&
        (entityType === EntityType.DEAL || entityType === EntityType.LEAD)
      ) {
        const partnerValue = gatingPartnerFieldKey
          ? entityData?.[gatingPartnerFieldKey]
          : undefined;
        if (!partnerValue) {
          return false;
        }
      }
      if (
        field.displayType === FieldDisplayType.PARTNERS &&
        (entityType === EntityType.DEAL || entityType === EntityType.LEAD)
      ) {
        return false;
      }
      if (field.fieldKey === "name" && entityType === EntityType.PARTNER) {
        return false;
      }
      if (
        field.fieldKey === "closedAt" &&
        entityType === EntityType.DEAL &&
        entityData
      ) {
        const dealStatus = entityData.status;
        return (
          dealStatus === DealStatus.CLOSED_WON ||
          dealStatus === DealStatus.CLOSED_LOST
        );
      }

      return true;
    })
    .sort((a, b) => a.order - b.order)
    .forEach((apiField) => {
      const fieldKey = apiField.fieldKey;
      const fieldConfig = createFieldConfigFromAPI(
        apiField,
        entityData,
        entityType,
        t,
        thresholds,
        context,
        isNewFieldsUI
      );

      if (fieldConfig) {
        orderedConfig[fieldKey] = fieldConfig;
      }
    });

  const findFieldKeyByDisplayType = (displayType: FieldDisplayType) =>
    Object.keys(orderedConfig).find(
      (key) =>
        (orderedConfig[key] as { displayType?: string }).displayType ===
        displayType
    );

  const partnerContactFieldKey = findFieldKeyByDisplayType(
    FieldDisplayType.PARTNER_CONTACTS
  );
  // On Deal/Lead the Partner field renders in the top header, so it is filtered
  // out of orderedConfig above — resolve its key from the raw API fields so the
  // Partner Contact option list stays filtered by the selected partner.
  const partnerFieldKey = apiFields.find(
    (field) => field.displayType === FieldDisplayType.PARTNERS
  )?.fieldKey;
  if (partnerContactFieldKey && partnerFieldKey) {
    const partnerConfig = orderedConfig[partnerFieldKey];
    if (partnerConfig) {
      (partnerConfig as { dependencies?: string[] }).dependencies = [
        partnerContactFieldKey
      ];
    }
    // Also read by resolveCreatePartnerContactPartnerId to pre-fill the
    // partner-contact quick-create modal — keep the `partner` key in sync.
    (
      orderedConfig[partnerContactFieldKey] as {
        dependentValues?: Record<string, string | null>;
      }
    ).dependentValues = { partner: entityData?.[partnerFieldKey] ?? null };
  }

  return orderedConfig;
};
